import { useState, useEffect, useRef } from 'react'
import * as blazeface from '@tensorflow-models/blazeface'
import * as tf from '@tensorflow/tfjs-core'
import '@tensorflow/tfjs-backend-webgl'
import '@tensorflow/tfjs-backend-cpu'

const useFaceDetection = () => {
  const [isModelLoaded, setIsModelLoaded] = useState(false)
  const modelRef = useRef(null)

  useEffect(() => {
    const loadModels = async () => {
      try {
        console.log('[MODELS] Loading BlazeFace...')

        // Set backend to webgl (preferred) with cpu fallback
        await tf.ready()
        console.log('[TF] Backend:', tf.getBackend())

        const faceModel = await blazeface.load()

        modelRef.current = faceModel
        setIsModelLoaded(true)
        console.log('[MODELS] BlazeFace loaded successfully')
      } catch (error) {
        console.error('[MODELS] Failed to load model:', error)
      }
    }

    loadModels()
  }, [])

  const detectFace = async (videoElement) => {
    if (!videoElement || !isModelLoaded || !modelRef.current) {
      return null
    }

    try {
      const width = videoElement.videoWidth
      const height = videoElement.videoHeight

      if (width === 0 || height === 0) {
        return null
      }

      const facePredictions = await modelRef.current.estimateFaces(videoElement, false)

      // No face detected
      if (!facePredictions || facePredictions.length === 0) {
        return null
      }

      // Use first detected face
      const face = facePredictions[0]

      // Extract bounding box coordinates
      // BlazeFace returns: topLeft [x, y] and bottomRight [x, y]
      const [x1, y1] = face.topLeft
      const [x2, y2] = face.bottomRight

      const faceWidth = x2 - x1
      const faceHeight = y2 - y1
      const xCenter = x1 + faceWidth / 2
      const yCenter = y1 + faceHeight / 2

      // Calculate face size (use width as primary metric for proximity)
      const faceSize = faceWidth

      return {
        box: {
          xMin: x1,
          yMin: y1,
          width: faceWidth,
          height: faceHeight,
          xCenter,
          yCenter
        },
        size: faceSize, // Used for proximity calculation
        probability: face.probability ? face.probability[0] : 1
      }
    } catch (err) {
      console.error('[DETECTION] Error:', err)
      return null
    }
  }

  return {
    detectFace,
    isModelLoaded
  }
}

export default useFaceDetection
